import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import { Features } from '~/components/FeatureToggle';
import TabModal, { TabItem } from '~/components/Modal/TabModal';
import useSessionData from '~/hooks/session/useSessionData';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import AppConfig from '~/utils/appconfig';
import AdaptConnectorWorkflow from './adapt/AdaptConnectorWorkflow';
import ContourConnectorWorkflow from './contour/ContourConnectorWorkflow';
import DataUpload from './data-upload';
import DosePredictionConnectorWorkflow from './dose/DosePredictionConnectorWorkflow';
import ImageTranslationConnectorWorkflow from './image/ImageTranslationConnectorWorkflow';

type AutoSegmentationModalProps = {
    sessionId: string;
};

export default function ConnectorWorkflows({
    sessionId,
}: AutoSegmentationModalProps) {
    const { environmentObjectIds } = useViewportEnvironment();
    const [workflowModal, setWorkflowModal] =
        useRecoilState(workflowModalState);

    const { session, isLoading, error } = useSessionData(sessionId);

    useEffect(() => {
        setWorkflowModal({
            open: true,
            page: WorkflowTypes.CONTOUR,
        });
    }, [setWorkflowModal]);

    const items = [
        AppConfig.isFeatureEnabled(Features.CONTOUR_WORKFLOW) && {
            name: <FormattedMessage id="contourPlus" />,
            description: <FormattedMessage id="contourPlusDescription" />,
            content: (
                <ContourConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.CONTOUR,
        },
        AppConfig.isFeatureEnabled(Features.DOSE_WORKFLOW) && {
            name: <FormattedMessage id="dosePlus" />,
            description: <FormattedMessage id="dosePlusDescription" />,
            content: (
                <DosePredictionConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.DOSE,
        },
        AppConfig.isFeatureEnabled(Features.IMAGE_TRANSLATION_WORKFLOW) && {
            name: <FormattedMessage id="imagePlus" />,
            description: <FormattedMessage id="imagePlusDescription" />,
            content: (
                <ImageTranslationConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.ADAPT,
        },
        AppConfig.isFeatureEnabled(Features.ADAPT_WORKFLOW) && {
            name: <FormattedMessage id="adaptPlus" />,
            description: <FormattedMessage id="adaptPlusDescription" />,
            content: (
                <AdaptConnectorWorkflow
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    isLoadingSession={isLoading}
                    error={error}
                />
            ),
            type: WorkflowTypes.IMAGE,
        },
        AppConfig.isFeatureEnabled(Features.DATA_UPLOAD) && {
            name: <FormattedMessage id="shareData" />,
            description: <FormattedMessage id="shareDataDescription" />,
            content: (
                <DataUpload
                    sessionData={session}
                    environmentObjectIds={environmentObjectIds}
                    showPseudonymization={false}
                />
            ),
            type: WorkflowTypes.DATA_UPLOAD,
        },
    ].filter(Boolean) as TabItem[];

    return (
        <TabModal
            open={workflowModal.open}
            setOpen={(open) => setWorkflowModal({ ...workflowModal, open })}
            items={items}
            selectedPage={workflowModal.page}
            setSelectedPage={(page) =>
                setWorkflowModal({
                    ...workflowModal,
                    page: page as WorkflowTypes,
                })
            }
        />
    );
}
